import * as React from "react";
import dayjs from "dayjs";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { TextField, InputAdornment } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { focusElemnt } from "../utils/helpers";

export default function CustomDatePicker({
    label = "Select date",
    selectedDate,
    onChange,
    minDate = null,
    maxDate = null,
    disabled = false,
    onKeyDown,
    id,
    nextId,
    prevId,
}) {
    const [open, setOpen] = React.useState(false);
    const justClosedRef = React.useRef(false);

    const value = selectedDate ? dayjs(selectedDate) : null;

    const handleChange = (newValue) => {
        onChange?.(newValue);
        justClosedRef.current = false;
        setOpen(false);
        focusElemnt(nextId);
    };

    const handleKeyDown = (e) => {
        if (disabled) return;

        if (e.key === "Enter") {
            e.preventDefault();
            if (open) {
                const selectedValue = value || dayjs();
                handleChange(selectedValue);
            } else {
                setOpen(true);
            }
            return;
        }

        if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
            justClosedRef.current = true;
            focusElemnt(prevId);
            return;
        }

        onKeyDown?.(e);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
                enableAccessibleFieldDOMStructure={false}
                label={label}
                value={value}
                open={open}
                format="ddd MMM DD YYYY"
                disabled={disabled}
                minDate={minDate}
                maxDate={maxDate}
                onChange={handleChange}
                onClose={() => {
                    setOpen(false);
                    justClosedRef.current = true
                    focusElemnt(nextId)
                }}
                slots={{
                    textField: (props) => <TextField {...props} />,
                }}
                slotProps={{
                    textField: {
                        id,
                        fullWidth: true,
                        inputProps: {
                            readOnly: true,
                            onKeyDown: handleKeyDown,
                        },
                        onClick: () => !disabled && setOpen(true),
                        InputProps: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarTodayIcon />
                                </InputAdornment>
                            ),
                        },
                        onFocus: () => {
                            if (disabled) return;
                            if (justClosedRef.current) {
                                justClosedRef.current = false;
                                return;
                            }
                            setOpen(true);
                        },
                        sx: {
                            "& label.Mui-focused": { color: "#0085FE" },
                            "& .MuiOutlinedInput-root": {
                                fontSize: "13px",
                                fontWeight: 600,
                                "& fieldset": {
                                    borderColor: open ? "#0085FE" : "#a6a8ab",
                                },
                                "&:hover fieldset": {
                                    borderColor: open ? "#0085FE" : "#a6a8ab",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "#0085FE",
                                },
                            },
                        },
                        InputLabelProps: {
                            sx: { color: open ? "#004F9E" : "#666" },
                        },
                    },
                    paper: {
                        sx: { mt: 1, borderRadius: 1 },
                    },
                }}
            />
        </LocalizationProvider>
    );
}